import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { ImageZoomModal, ZoomableMedia } from '~/components/molecules/ImageZoomModal';
import { useEvent } from '~/hooks/useEvent';
import { isSSR } from '~/utils/client-utils';

interface ImageViewerContextProps {
  openImageViewer: (images: ZoomableMedia[], initialIndex?: number, title?: string) => void;
  closeImageViewer: () => void;
}

const ImageViewerContext = createContext<ImageViewerContextProps>({
  openImageViewer: () => { },
  closeImageViewer: () => { },
});

export const useImageViewer = () => useContext(ImageViewerContext);

interface ImageViewerProviderProps {
  children: ReactNode;
}

export const ImageViewerProvider: React.FC<ImageViewerProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [images, setImages] = useState<ZoomableMedia[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [title, setTitle] = useState('');
  const { sendEvent } = useEvent();

  const openImageViewer = useCallback((images: ZoomableMedia[], initialIndex = 0, title = '') => {
    setImages(images);
    setCurrentIndex(initialIndex);
    setTitle(title);
    setIsOpen(true);
    sendEvent('image_viewer_open', {
      totalImages: images.length,
      initialIndex,
      contextId: title || 'unknown',
    });
  }, [sendEvent]);

  const closeImageViewer = useCallback(() => {
    sendEvent('image_viewer_close', {
      totalImages: images.length,
      currentIndex,
      contextId: title || 'unknown',
    });
    setIsOpen(false);
  }, [sendEvent, images.length, currentIndex, title]);

  const value = {
    openImageViewer,
    closeImageViewer,
  };

  return (
    <ImageViewerContext.Provider value={value}>
      {children}
      {!isSSR && (
        <ImageZoomModal
          isOpen={isOpen}
          onClose={closeImageViewer}
          media={images}
          initialIndex={currentIndex}
          title={title}
        />
      )}
    </ImageViewerContext.Provider>
  );
}; 